/**
 * One notice, as the bell and the notifications page show it: an icon for its kind, what
 * happened, the facts, and when. A notice with a link is one link to the page it is about.
 */

import {
  Bell,
  ChartColumn,
  CircleCheck,
  CircleX,
  HandCoins,
  Hourglass,
  Inbox,
  Link2,
  type LucideIcon,
  Megaphone,
  MessageSquareWarning,
  Send,
  Volume2,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { formatAgo, formatUtc } from "../format.js";
import { isExternalLink, type Notification } from "./notifications.js";

/** The icon of each subject of a notice, for the kinds that have none of their own. */
const SUBJECT_ICONS: Record<string, LucideIcon> = {
  admin: Inbox,
  request: Inbox,
  submission: Send,
  issue: MessageSquareWarning,
  campaign: Megaphone,
  result: ChartColumn,
  payout: HandCoins,
  account: Link2,
};

/** Kinds that tell of money that came back or was paid. */
const MONEY = new Set([
  "campaign.cancelled",
  "campaign.voided",
  "campaign.refunded",
  "campaign.refund_withdrawn",
]);

/** The icon of a notice, by what happened first, and by its kind's subject for the rest. */
function iconOf(kind: string): LucideIcon {
  if (kind === "announcement") {
    return Volume2;
  }
  if (MONEY.has(kind)) {
    return HandCoins;
  }
  if (kind === "request.lapsed") {
    return Hourglass;
  }
  if (/[._](accepted|upheld|funded|settled)$/.test(kind)) {
    return CircleCheck;
  }
  if (/[._](rejected|failed)$/.test(kind)) {
    return CircleX;
  }
  return SUBJECT_ICONS[kind.split(".")[0] ?? ""] ?? Bell;
}

/**
 * @param compact The bell's rows, with less room. The page's rows show the exact time too.
 * @param onOpen Called when the reader follows the notice's link.
 */
export function NotificationItem({
  notice,
  compact = false,
  onOpen,
}: {
  notice: Notification;
  compact?: boolean;
  onOpen?: () => void;
}) {
  const Icon = iconOf(notice.kind);
  const recent = Date.now() - Date.parse(notice.createdAt) < 7 * 86_400_000;
  const content = (
    <span className="flex gap-3">
      <span
        aria-hidden
        className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full ring-1 ring-inset ${
          notice.unread
            ? "bg-brand-500/10 text-brand-700 ring-brand-500/25 dark:bg-brand-400/10 dark:text-brand-300 dark:ring-brand-400/25"
            : "bg-zinc-900/[0.04] text-zinc-500 ring-zinc-900/[0.08] dark:bg-white/[0.05] dark:text-zinc-400 dark:ring-white/10"
        }`}
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start justify-between gap-2">
          <span
            className={`text-sm text-zinc-950 dark:text-white ${notice.unread ? "font-semibold" : "font-medium"}`}
          >
            {notice.title}
          </span>
          {notice.unread && (
            <>
              <span
                aria-hidden
                className="mt-1.5 size-2 shrink-0 rounded-full bg-brand-500 dark:bg-brand-400"
              />
              <span className="sr-only">New</span>
            </>
          )}
        </span>
        <span
          className={`mt-0.5 block text-sm text-zinc-600 dark:text-zinc-400 ${compact ? "line-clamp-2" : ""}`}
        >
          {notice.body}
        </span>
        <span className="mt-1 block text-xs text-zinc-500 dark:text-zinc-500">
          <time dateTime={notice.createdAt} title={formatUtc(notice.createdAt)}>
            {/* After a week "ago" is the date itself, so the page shows the exact time alone. */}
            {compact || recent ? formatAgo(notice.createdAt) : formatUtc(notice.createdAt)}
          </time>
          {!compact && recent && ` · ${formatUtc(notice.createdAt)}`}
        </span>
      </span>
    </span>
  );
  const frame = `block rounded-xl px-3 py-2.5 text-left ${compact ? "" : "sm:px-4 sm:py-3.5"}`;
  const hover =
    "transition-colors hover:bg-zinc-900/[0.04] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:hover:bg-white/[0.05]";
  return (
    <Framed link={notice.link} className={frame} hover={hover} onOpen={onOpen}>
      {content}
    </Framed>
  );
}

/** The notice's frame: a link when it leads somewhere, and plain when it does not. */
function Framed({
  link,
  className,
  hover,
  onOpen,
  children,
}: {
  link: string | null;
  className: string;
  hover: string;
  onOpen?: (() => void) | undefined;
  children: ReactNode;
}) {
  if (link === null) {
    return <div className={className}>{children}</div>;
  }
  if (isExternalLink(link)) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noreferrer"
        onClick={onOpen}
        className={`${className} ${hover}`}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={link} onClick={onOpen} className={`${className} ${hover}`}>
      {children}
    </Link>
  );
}
